import { forwardRef, memo } from 'react';

import type { TreeItemProps } from '@mui/x-tree-view-pro';

import { SHOW_MORE_SUFFIX } from '../constants';
import type { ExtraItemProps } from '../types';
import { isErrorRow, isPlaceholder, isShowMore } from '../utils/treeId';
import { DataNodeItem } from './tree-items/DataNodeItem';
import { ErrorItem } from './tree-items/ErrorItem';
import { LoadingPlaceholderItem } from './tree-items/LoadingPlaceholderItem';
import { ShowMoreItem } from './tree-items/ShowMoreItem';

type CustomTreeItemAllProps = TreeItemProps & ExtraItemProps;

const CustomTreeItemImpl = forwardRef<HTMLLIElement, CustomTreeItemAllProps>(function CustomTreeItem(props, ref) {
  const { itemId } = props;

  if (isPlaceholder(itemId)) {
    return <LoadingPlaceholderItem {...props} ref={ref} />;
  }
  if (isShowMore(itemId)) {
    return <ShowMoreItem {...props} ref={ref} />;
  }
  if (isErrorRow(itemId)) {
    return <ErrorItem {...props} ref={ref} />;
  }
  return <DataNodeItem {...props} ref={ref} />;
});

/**
 * Memo comparator for `CustomTreeItem`.
 *
 * Default shallow comparison would defeat memoization because `itemMetaMap`
 * and `loadingParentIds` change reference on every page-level render. Instead
 * we compare the props this component actually reads:
 *
 *  - `itemMetaMap[itemId]` — per-entry identity is stable across rebuilds
 *    thanks to the cache in `useTreeItems`, so an `===` check on the
 *    single relevant entry tells us whether *this* item's meta changed.
 *  - `loadingParentIds` — only Show-more synthetic items read this, so for
 *    every other item we skip it entirely.
 *  - `children` — falls back to identity comparison so MUI subtree updates
 *    propagate normally (leaves have `children === undefined`, so memo helps
 *    them; branch items re-render whenever their subtree changes, which is
 *    correct).
 *
 * Trade-off: this comparator only knows about the props we explicitly listed,
 * so if MUI ever starts passing a new dynamic prop to the item slot we'd miss
 * it and the item could fail to re-render. Today MUI manages expansion /
 * focus / selection via context, not via per-item props, so this is safe;
 * revisit if we upgrade `@mui/x-tree-view-pro` significantly.
 */
function arePropsEqual(prev: Readonly<CustomTreeItemAllProps>, next: Readonly<CustomTreeItemAllProps>): boolean {
  if (prev.itemId !== next.itemId) return false;
  if (prev.label !== next.label) return false;
  if (prev.children !== next.children) return false;
  if (prev.callbacks !== next.callbacks) return false;
  if (prev.nodeIcon !== next.nodeIcon) return false;
  if (prev.nodeName !== next.nodeName) return false;
  if (prev.isMoveMode !== next.isMoveMode) return false;
  if (prev.selectedMoveNodeId !== next.selectedMoveNodeId) return false;
  if (prev.disabledParentTreeId !== next.disabledParentTreeId) return false;
  if (prev.selectedParentId !== next.selectedParentId) return false;

  const wasMovedNodeHighlighted = prev.isMoveMode && prev.selectedMoveNodeId === prev.itemId;
  const isMovedNodeHighlightedNow = next.isMoveMode && next.selectedMoveNodeId === next.itemId;
  if (wasMovedNodeHighlighted !== isMovedNodeHighlightedNow) return false;

  if (prev.onSelectParent !== next.onSelectParent) return false;
  if (prev.testIdScope !== next.testIdScope) return false;

  if (prev.itemMetaMap?.[prev.itemId] !== next.itemMetaMap?.[next.itemId]) return false;

  if (isShowMore(prev.itemId)) {
    if (prev.onShowMore !== next.onShowMore) return false;
    const parentId = prev.itemId.slice(0, -SHOW_MORE_SUFFIX.length);
    const wasLoading = prev.loadingParentIds?.has(parentId) ?? false;
    const isLoadingNow = next.loadingParentIds?.has(parentId) ?? false;
    if (wasLoading !== isLoadingNow) return false;
  }

  return true;
}

export const CustomTreeItem = memo(CustomTreeItemImpl, arePropsEqual);
